
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface EmployeeDashboardData {
  employee: {
    id: string;
    name: string;
    position: string;
    department: string;
    email: string | null;
    phone: string | null;
    status: string;
    join_date: string;
    birth_date: string | null;
    qualifications: string | null;
    previous_experience: string | null;
    electronic_accounts: any;
    job_title: string | null;
    job_responsibilities: string | null;
    job_description: string | null;
    employee_number: string | null;
    hiring_date: string | null;
    working_hours: number | null;
    salary: number | null;
    profile_image: string | null;
  };
  clients: Array<{
    id: string;
    name_ar: string;
    status: string;
    created_at: string;
  }>;
  packages: Array<{
    id: string;
    status: string;
  }>;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    due_date: string | null;
    created_at: string;
  }>;
  stats: {
    totalClients: number;
    activeClients: number;
    completedPackages: number;
    pendingTasks: number;
    monthlyPerformance: number;
  };
}

export const useEmployeeDashboard = (employeeId?: string) => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["employee-dashboard", employeeId],
    queryFn: async (): Promise<EmployeeDashboardData> => {
      if (!employeeId) {
        throw new Error("Employee ID is required");
      }

      console.log("Fetching dashboard data for employee:", employeeId);

      // Fetch employee data
      const { data: employee, error: employeeError } = await supabase
        .from("employees")
        .select("*")
        .eq("id", employeeId)
        .single();

      if (employeeError) {
        console.error("Error fetching employee:", employeeError);
        throw employeeError;
      }

      // جلب العملاء الذين أنشأهم هذا الموظف فقط
      const { data: clients, error: clientsError } = await supabase
        .from("clients")
        .select("id, name_ar, status, created_at")
        .eq("created_by", employeeId);

      if (clientsError) {
        console.error("Error fetching clients:", clientsError);
        throw clientsError;
      }

      console.log("Clients created by employee:", clients?.length || 0);

      // جلب الحجوزات التي أنشأها هذا الموظف فقط
      const { data: bookings, error: bookingsError } = await supabase
        .from("package_bookings")
        .select("id, client_id, status")
        .eq("employee_id", employeeId);

      if (bookingsError) {
        console.error("Error fetching bookings:", bookingsError);
        throw bookingsError;
      }

      // جلب المهام المسندة لهذا الموظف فقط
      const { data: tasks, error: tasksError } = await supabase
        .from("tasks")
        .select("id, title, status, priority, due_date, created_at")
        .eq("assigned_to", employeeId)
        .order("created_at", { ascending: false })
        .limit(10);

      if (tasksError) {
        console.error("Error fetching tasks:", tasksError);
        throw tasksError;
      }

      // حساب الإحصائيات الخاصة بهذا الموظف فقط
      const totalClients = clients?.length || 0;
      const activeClients = clients?.filter(c => c.status !== 'cancelled' && c.status !== 'completed').length || 0;
      const completedPackages = bookings?.filter(b => b.status === 'completed').length || 0;
      const pendingTasks = tasks?.filter(t => t.status !== 'completed' && t.status !== 'cancelled').length || 0;

      // الأداء الشهري من المهام المكتملة خلال الشهر الماضي
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const recentTasks = tasks?.filter(t => t.created_at && new Date(t.created_at) >= thirtyDaysAgo) || [];
      const completedRecentTasks = recentTasks.filter(t => t.status === 'completed').length;
      const monthlyPerformance = recentTasks.length > 0 ? Math.round((completedRecentTasks / recentTasks.length) * 100) : 0;

      console.log("Dashboard stats:", { totalClients, activeClients, completedPackages, pendingTasks });

      return {
        employee,
        clients: clients || [],
        packages: bookings || [],
        tasks: tasks?.map(t => ({...t, due_date: t.due_date, created_at: t.created_at! })) || [],
        stats: {
          totalClients,
          activeClients,
          completedPackages,
          pendingTasks,
          monthlyPerformance,
        }
      };
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
